<template>
  <section>
    <Feature :title="$t('QUOTATION_DATA_DETAIL')" :iconClass="'fa-check-square'">
      <FormRow v-if="status === 'PROCESSING'" :fieldNameArr="[$t('SELECT_ALL'), '']">
        <RadioGroup v-model="selectAllType" @on-change="changeSelectAll" slot="a">
          <Radio label="APPROVE">{{ $t("APPROVE") }}</Radio>
          <Radio label="REJECT">{{ $t("REJECT") }}</Radio>
        </RadioGroup>
        <Button type="primary" @click="cleanSelect" slot="b">{{ $t('CLEAR_SELECT') }}</Button>
      </FormRow>
      <FormRow :fieldNameArr="[ $t('LAYOUT') ]">
        <RadioGroup v-model="layoutType" slot="a">
          <Radio label="SIMPLE">{{ $t("SIMPLE") }}</Radio>
          <Radio label="DEFAULT">{{ $t("DEFAULT") }}</Radio>
        </RadioGroup>
      </FormRow>
      <Table v-show="layoutType === 'SIMPLE'" :columns="quotationColumnListForSimple" :data="quotationList" row-key="key">
        <template slot-scope="{ row, index }" slot="clear">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Button shape="circle" size="small" style="width: 80%" @click="clearAction(index)">{{ $t("CLEAR") }}</Button>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="approve">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Radio v-model="quotationList[index].selectApprove" style="display: inline-block; font-size: 14px;" @on-change="value => changeApprove(value, index)">{{ $t("APPROVE") }}</Radio>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="reject">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Radio v-model="quotationList[index].selectReject" style="display: inline-block; font-size: 14px;" @on-change="value => changeReject(value, index)">{{ $t("REJECT") }}</Radio>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="comments">
          <Icon v-if="row.resourceType !== 'AbnormalQuotationDto'" type="ios-create" size="28" class="text-delta-blue" @click="showCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="historyComments">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showHistoryCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="quotationFiles">
          <Icon v-if="row.isCoverQuote && row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="md-close-circle" size="28" class="text-delta-red" @click="showQuotationFilesModal(index)" />
          <Icon v-else-if="row.isCoverQuote" type="md-close-circle" size="28" class="text-delta-red" />
          <Icon v-else-if="row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="ios-cloud-download" size="28" class="text-delta-blue" @click="showQuotationFilesModal(index)"/>
        </template>
        <template slot-scope="{ row, index }" slot="referenceFile">
          <Icon :type="row.cpcToSupervisorAttachments && row.cpcToSupervisorAttachments.filter(attachement => !attachement.deleted).length > 0 ? 'ios-cloud-download' : ''" size="28" class="text-delta-blue" @click="showReferenceFileModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="plmFiles">
          <Icon type="md-download" size="28" class="text-delta-blue" @click="showPlmFilesModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="validFrom">
          <DatePicker :disabled="status !== 'PROCESSING'" :transfer="true" v-model="quotationList[index].validFrom" type="date" format="yyyy-MM-dd" placeholder="Select Date" @on-change="updateDate($event, index, 'validFrom')"></DatePicker>
        </template>
        <template slot-scope="{ row, index }" slot="validTo">
          <DatePicker :disabled="status !== 'PROCESSING'" :transfer="true" v-model="quotationList[index].validTo" type="date" format="yyyy-MM-dd" placeholder="Select Date" @on-change="updateDate($event, index, 'validTo')"></DatePicker>
        </template>
        <template slot-scope="{ row, index }" slot="highPrice">
          <div v-if="row.highPrice && row.quotationHighPrice">
            <Icon type="ios-warning" size="28" class="text-delta-red" />
            <span>{{ row.quotationHighPrice.priceDifference }}%</span>
            <br/>
            <span>{{ row.quotationHighPrice.orgCheck === 'C' ? 'Cross' : 'Single' }}</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="unitPriceAndCurrency">
          <div v-if="(row.abnormals && row.abnormals.quoteRedAlert) || row.quoteRedAlert">
            <Button type="error" style="width: 80%" shape="circle" size="small">{{ row.unitPrice }}</Button>
            <span>/{{ row.currency }}</span>
          </div>
          <div v-else>{{ row.price ? row.price : "" }}/{{ row.currency ? row.currency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="referencePriceData">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showReferencePriceDataModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="itemCategory">
          <span v-if="quotationList[index].itemCategory === 'STANDARD'">Standard</span>
          <span v-if="quotationList[index].itemCategory === 'SUBCONTRACTION'">Subcontraction</span>
        </template>
        <template slot-scope="{ row, index }" slot="averagePurchasePriceAndCurrency">
          <div>{{ row.averagePurchasePrice ? row.averagePurchasePrice : "" }}/{{ row.averagePurchaseCurrency ? row.averagePurchaseCurrency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="scalePrice">
          <Icon :type="row.quotationScalePriceList && row.quotationScalePriceList.length > 0 ? 'md-reorder' : ''" size="28" class="text-delta-blue" @click="showScalePriceModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="rawMaterialPriceAndUnit">
          <div>{{ row.rawMaterialPrice ? row.rawMaterialPrice : "" }}/{{ row.rawMaterialUnit ? row.rawMaterialUnit : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="remark">
          <Icon v-if="row.resourceType !== 'AbnormalQuotationDto'" type="ios-create" size="28" class="text-delta-blue" @click="showRemarkModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="ymgrRemark">
          <div>{{ row.ymgrRemark }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="log">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showLogModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="categoryAttributes">
          <Icon v-if="row.category && row.category !== 'OTHERS'" type="ios-paper" size="28" class="text-delta-blue" @click="showCategoryAttributesModal(index, row.category, row.formNo)" />
        </template>
        <template slot-scope="{ row, index }" slot="infoRecord">
          <div v-if="row.infoRecordFlag">
            <span>YES</span>
            <Icon type="ios-create" size="28" class="text-delta-blue" @click="showInfoRecordMaintainModal(index, row.uuid)" />
          </div>
          <div v-else>
            <span>NO</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="rfqType">
          <span>{{ row.rfqType.description }}</span>
        </template>
      </Table>
      <Table v-show="layoutType === 'DEFAULT'" :columns="quotationColumnList" :data="quotationList" row-key="key" class="overflow-visible">
        <template slot-scope="{ row, index }" slot="action">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Radio v-model="quotationList[index].selectApprove" style="display: inline-block; font-size: 14px;" @on-change="value => changeApprove(value, index)">{{ $t("APPROVE") }}</Radio>
            <Radio v-model="quotationList[index].selectReject" style="display: inline-block; font-size: 14px;" @on-change="value => changeReject(value, index)">{{ $t("REJECT") }}</Radio>
            <Button shape="circle" size="small" style="width: 80%" @click="clearAction(index)">{{ $t("CLEAR") }}</Button>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="comments">
          <Icon v-if="row.resourceType === 'SupervisorQuotationDetailDto'" type="ios-create" size="28" class="text-delta-blue" @click="showCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="historyComments">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showHistoryCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="rfqType">
          <span>{{ row.rfqType.description }}</span>
        </template>
        <template slot-scope="{ row, index }" slot="infoRecord">
          <div v-if="row.infoRecordFlag">
            <span>YES</span>
            <Icon type="ios-create" size="28" class="text-delta-blue" @click="showInfoRecordMaintainModal(index, row.uuid)" />
          </div>
          <div v-else>
            <span>NO</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="highPrice">
          <div v-if="row.highPrice && row.quotationHighPrice">
            <Icon type="ios-warning" size="28" class="text-delta-red" />
            <span>{{ row.quotationHighPrice.priceDifference }}%</span>
            <br/>
            <span>{{ row.quotationHighPrice.orgCheck === 'C' ? 'Cross' : 'Single' }}</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="deltaPartAndDeltaPartDescription">
          <div>{{ row.deltaPart ? row.deltaPart : "" }}<br>{{ row.deltaPartDesc ? row.deltaPartDesc : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="vendorCodeAndVendorName">
          <div>{{ row.vendorCode ? row.vendorCode : "" }}<br>{{ row.vendorName ? row.vendorName : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="unitPriceAndCurrency">
          <div v-if="(row.abnormals && row.abnormals.quoteRedAlert) || row.quoteRedAlert">
            <Button type="error" style="width: 80%" shape="circle" size="small">{{ row.price }}</Button>
            <span>/{{ row.currency }}</span>
          </div>
          <div v-else>{{ row.price ? row.price : "" }}/{{ row.currency ? row.currency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="averagePurchasePriceAndCurrency">
          <div>{{ row.averagePrice ? row.averagePrice : "" }}/{{ row.averagePriceCurrency ? row.averagePriceCurrency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="plmFiles">
          <Icon type="md-download" size="28" class="text-delta-blue" @click="showPlmFilesModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="quotationFiles">
          <Icon v-if="row.isCoverQuote && row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="md-close-circle" size="28" class="text-delta-red" @click="showQuotationFilesModal(index)" />
          <Icon v-else-if="row.isCoverQuote" type="md-close-circle" size="28" class="text-delta-red" />
          <Icon v-else-if="row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="ios-cloud-download" size="28" class="text-delta-blue" @click="showQuotationFilesModal(index)"/>
        </template>
        <template slot-scope="{ row, index }" slot="referenceFile">
          <Icon :type="row.cpcToSupervisorAttachments && row.cpcToSupervisorAttachments.filter(attachement => !attachement.deleted).length > 0 ? 'ios-cloud-download' : ''" size="28" class="text-delta-blue" @click="showReferenceFileModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="scalePrice">
          <Icon :type="row.scalePrices && row.scalePrices.length > 0 ? 'md-reorder' : ''" size="28" class="text-delta-blue" @click="showScalePriceModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="referencePriceData">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showReferencePriceDataModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="rawMaterialPriceAndUnit">
          <div>{{ row.rawMaterialPrice ? row.rawMaterialPrice : "" }}/{{ row.rawMaterialUnit ? row.rawMaterialUnit : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="remark">
          <Icon v-if="row.resourceType === 'SupervisorQuotationDetailDto'" type="ios-create" size="28" class="text-delta-blue" @click="showRemarkModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="ymgrRemark">
          <div>{{ row.ymgrRemark }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="log">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showLogModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="categoryAttributes">
          <Icon v-if="row.category && row.category !== 'OTHERS'" type="ios-paper" size="28" class="text-delta-blue" @click="showCategoryAttributesModal(index, row.category, row.formNo)" />
        </template>
        <template slot-scope="{ row, index }" slot="itemCategory">
          <span v-if="quotationList[index].itemCategory === 'STANDARD'">Standard</span>
          <span v-if="quotationList[index].itemCategory === 'SUBCONTRACTION'">Subcontraction</span>
        </template>
        <template slot-scope="{ row, index }" slot="validFromAndValidTo">
          <DatePicker :disabled="status !== 'PROCESSING'" v-model="quotationList[index].validFrom" type="date" format="yyyy-MM-dd" placeholder="Select Date" placement="right-start" @on-change="updateDate($event, index, 'validFrom')"></DatePicker>
          <DatePicker :disabled="status !== 'PROCESSING'" v-model="quotationList[index].validTo" type="date" format="yyyy-MM-dd" placeholder="Select Date" placement="right-start" @on-change="updateDate($event, index, 'validTo')"></DatePicker>
        </template>
      </Table>
      <ButtonRow>
        <Button v-if="status === 'PROCESSING'" type="primary" @click="Submit" slot="1">{{ $t('SUBMIT') }}</Button>
        <Button type="primary" @click="Back" slot="2">{{ $t('BACK') }}</Button>
      </ButtonRow>
    </Feature>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="logModel"
      :title="$t('PROCESS_LOG')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <TablePage :columnsList="quotationLogColumnList" :resultList="quotationLogList" :tableMaxHeight="800"></TablePage>
      </div>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="plmFileModel"
      :title="$t('PLM_FILES')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <PLMDocument :plmQuotationDocuments="quotationDocuments" :quotationUuid="quotationUuid"></PLMDocument>
      </div>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" width="900" v-model="supplierQuotationFilesModel" sticky
      :draggable="true" :title="$t('QUOTATION_FILE')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <FileDocument :attachments="supplierQuotationFiles"></FileDocument>
      </div>
    </Modal>
    <Modal :z-index="9999" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="scalePricesModel"
      :title="$t('SCALE_PRICE')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <Table :columns="scalePriceColumnList" :data="scalePrices" max-height="800"></Table>
      </div>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="commentsModel"
      :title="$t('COMMENTS')" @on-ok="confirmComments">
      <froala :config="FloraEditorConfig" v-model="comments"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="remarkModel"
      :title="$t('REMARK')" @on-ok="confirmRemark">
      <froala :config="FloraEditorConfig" v-model="remark"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900"
      v-model="referencePriceModel" :title="$t('REFERENCE_PRICE_DATA')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <Table :columns="referencePriceColumnList" :data="referencePrices" max-height="800"></Table>
      </div>
    </Modal>
    <Modal :z-index="999" :reset-drag-position="true" width="900" v-model="cpcToSupervisorAttachmentsModel" sticky
      :draggable="true" :title="$t('REFERENCE_FILE')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <FileDocument :attachments="cpcToSupervisorAttachments"></FileDocument>
      </div>>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900"
      v-model="flowCommentModel" :title="$t('COMMENTS')">
      <froala :config="FloraEditorConfig" v-model="flowComment"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="logCommentModel"
      :title="$t('HISTORY') + ' ' + $t('COMMENTS')">
      <froala :config="FloraEditorConfig" v-model="logComment"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="1300"
      v-model="metalAttributeModel" :title="$t('METAL_CASE_ATTRIBUTES')">
      <MetalAttributes :metalQuotation="metalQuotation" :referencePrice="referencePrice" :priceList="priceList">
      </MetalAttributes>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="1300"
      v-model="plasticAttributeModel" :title="$t('PLASTIC_CASE_ATTRIBUTES')">
      <PlasticAttributes :plasticQuotation="plasticQuotation" :referencePrice="referencePrice" :priceList="priceList">
      </PlasticAttributes>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="1300"
      v-model="pwbAttributeModel" :title="$t('PWB_CASE_ATTRIBUTES')">
      <PwbAttributes :pwbQuotation="pwbQuotation" :priceList="priceList"></PwbAttributes>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="infoRecordMaintainModal" :title="$t('INFO_RECORD_MAINTAIN')">
      <InfoRecordMaintain v-if="infoRecordMaintainModal" :quotation="quotation" :materialTypeList="materialTypeList"></InfoRecordMaintain>
    </Modal>
  </section>
</template>

<script>
import { mapGetters } from "vuex"
import { mapActions } from "vuex"
import PLMDocument from '../../components/PLMDocument.vue'
import FileDocument from '../../components/FileDocument.vue'
import { Translate, FloraEditorConfig } from "@/util/common"
import { AlertInfo } from "@/util/alert"
import moment from "moment"
import MetalAttributes from "@/views/quotation/MetalAttributes"
import PlasticAttributes from "@/views/quotation/PlasticAttributes"
import PwbAttributes from "@/views/quotation/PwbAttributes"
import InfoRecordMaintain from "@/views/quotation/InfoRecordMaintain"

export default {
  name: "SupervisorMaintain",
  props: {
    status: {
      type: String
    }
  },
  components: {
    PLMDocument,
    FileDocument,
    MetalAttributes,
    PlasticAttributes,
    PwbAttributes,
    InfoRecordMaintain
  },
  created() {
    const vueInstance = this
    if (vueInstance.getSupervisorGroupUuid()) {
      vueInstance.selectAllType = "";
      vueInstance.defaultValidTo = getDefaultValidTo();
      vueInstance.toggleLoading(true);
      vueInstance.$quotationHttp
        .get("/supervisor/details/group/" + vueInstance.getSupervisorGroupUuid())
        .then(response => {
          if (!response.data || !response.data._embedded) {
            return;
          }
          vueInstance.quotationList = response.data._embedded.supervisorQuotationDetailResourceList
          vueInstance.quotationList.forEach(quotation => {
            if (quotation.itemCategory === null || quotation.itemCategory === undefined) {
              quotation.itemCategory = "STANDARD";
            }
            if (quotation.validFrom === null || quotation.validFrom === undefined) {
              quotation.validFrom = moment().format("YYYY-MM-DD");
            }
            if (quotation.validTo === null || quotation.validTo === undefined) {
              quotation.validTo = vueInstance.defaultValidTo;
            }
            vueInstance.$queryAbnormals(vueInstance, quotation.uuid)
              .then(data => {
                if (!data) {
                  return;
                }
                data.forEach(abnormalQuotation => {
                  abnormalQuotation.key = quotation.uuid + "_" + abnormalQuotation.uuid;
                });
                quotation.key = quotation.uuid;
                quotation.children = data;
                quotation._showChildren = true;
                let index = vueInstance.quotationList.findIndex(temp => temp.uuid == quotation.uuid);
                vueInstance.$set(vueInstance.quotationList, index, quotation);
              });
          });
        })
        .catch(() => {
          vueInstance.$router.push({
            name: "SUPERVISOR_MAINTAIN"
          });
        })
        .finally(() => {
          vueInstance.toggleLoading(false);
        });
    } else {
      vueInstance.$router.push({
        name: "SUPERVISOR_MAINTAIN"
      });
    }
  },
  data() {
    return {
      FloraEditorConfig: FloraEditorConfig(""),
      actionType: '',
      selectAllType: '',
      layoutType: "DEFAULT",
      quotationUuid: '',
      logModel: false,
      plmFileModel: false,
      supplierQuotationFilesModel: false,
      scalePricesModel: false,
      commentsModel: false,
      remarkModel: false,
      referencePriceModel: false,
      flowCommentModel: false,
      cpcToSupervisorAttachmentsModel: false,
      cpcToSupervisorAttachments: [],
      logComment: '',
      logCommentModel: false,
      flowComment: '',
      quotationDocuments: [],
      quotationLogList: [],
      supplierQuotationFiles: [],
      scalePrices: [],
      comments: '',
      remark: '',
      defaultValidTo: "",
      excelQuotationList: [],
      metalAttributeModel: false,
      metalQuotation: {
        length: '',
        width: '',
        height: '',
        weight: '',
        rawMaterialType: {
          partNo: '',
          partNoDesc: ''
        },
        rmVendor: {
          vendorCode: ''
        },
        coilCenter: {
          vendorCode: ''
        },
        rmPrice: '',
        rmPriceDate: '',
        rmCost: '',
        processingCost: '',
        treatmentCost: '',
        accessoriesCost: '',
        others: '',
        rmRatio: '',
        totalCost: '',
        supplierRemark: ''
      },
      plasticAttributeModel: false,
      plasticQuotation: {
        length: '',
        width: '',
        height: '',
        weight: '',
        model: '',
        resinType: {
          type: ''
        },
        resinVendor: {
          vendorCode: ''
        },
        resinNo: {
          partNo: '',
          partNoDesc: ''
        },
        distributor: {
          vendorCode: ''
        },
        resinColor: '',
        colorNo: '',
        rmPrice: '',
        rmPriceDate: '',
        dyeCost: '',
        cavity: '',
        tannage: '',
        hourlyRate: '',
        cycleTime: '',
        rmCost: '',
        injectingCost: '',
        processingCost: '',
        treatmentCost: '',
        accessoriesCost: '',
        others: '',
        rmRatio: '',
        totalCost: '',
        supplierRemark: ''
      },
      pwbAttributeModel: false,
      pwbQuotation: {
        deltaExchangeRate: "",
        cpcExchangeRate: "",
        rfqBase: "",
        cclLaminateType: "",
        layer: "",
        pcbThickness: "",
        internalCoreThickness: "",
        tg: "",
        externalCopperThickness: "",
        internalCopperThickness: "",
        mainSourceCclSupplier: {
          vendorCode: ""
        },
        mainSourceCclShare: 0,
        mainCoreUlNumber: {
          partNoDesc: ""
        },
        mainCoreUP: "",
        secondSourceCclSupplier: {
          vendorCode: ""
        },
        secondCoreUlNumber: {
          partNoDesc: ""
        },
        secondCoreUP: "",
        pcbPrice: "",
        panelLength: "",
        panelWidth: "",
        pnLength: "",
        pnWidth: "",
        area: "",
        pnArea: "",
        supplierSheetSize: "",
        deltaSheetSize: "",
        panelSheet: "",
        pnInSheet: "",
        pnInPanel: "",
        pnUtilization: "",
        panelUtilization: "",
        combineRatio: "",
        numberOfDrill: "",
        slotLength: "",
        goldArea: "",
        materialCost: "",
        surfaceFinishCost: "",
        processCost: "",
        specialCostDown: "",
        deltaPnPriceUsd: "",
        deltaPnPrice: "",
        supplierRemark: ""
      },
      referencePrice: '',
      priceList: [],
      referencePriceColumnList: [
        {
          renderHeader: (h, params) => {
            var text = this.$t('MATERIAL');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 140,
          key: "materialNumber",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('VENDOR_CODE') + "<br>" + this.$t('OA_VENDOR_NAME')
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 140,
          render: (h, params) => {
            let vendorCode = params.row.vendorCode != undefined ? params.row.vendorCode : ""
            let vendorName = params.row.vendorName != undefined ? params.row.vendorName : ""
            let text = vendorCode + "<br>" + vendorName
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          }
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('AGENT');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "agent",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('AGENT_NAME');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 140,
          key: "agentName",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('INFO_CATEGORY');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "purchInfoRecordCategory",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('VALID_FROM');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "validFromDate",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('VALID_TO');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "validToDate",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('PLANT_CODE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "plant",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('CURRENCY');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "currency",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('UNIT_PRICE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "unitPirce",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('USD_PRICE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "usdPrice",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('UOM');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "conditionUnit",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('%');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "percentage",
        },
      ],
      referencePrices: [],
      scalePriceColumnList: [
        {
          title: 'Item',
          key: 'item',
          align: 'center',
          width: 80
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('SCALE') + '<br>' + this.$t('QUANTITY')
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          key: 'scaleQuantity',
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('SCALE') + '<br>' + this.$t('PRICE')
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          key: 'scalePrice'
        }
      ],
      quotationLogColumnList: [
        {
          renderHeader: (h, params) => {
            var text = this.$t('ROLE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "quotationRole",
          width: 150,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('STATUS');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "flowStatus",
          width: 150,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('GROUP_ACCOUNT');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "groupAccount",
          width: 180,
          align: "center",
          render: (h, params) => {
            return h("span", {}, params.row["groupAccount"].companyName)
          }
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('ACTION');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "flowAction",
          width: 150,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('COMMENTS');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          width: 150,
          align: "center",
          render: (h, params) => {
            if (params.row.flowComment) {
              return h("Icon", {
                props: {
                  type: "ios-paper",
                  size: 28,
                },
                class: "text-delta-blue",
                on: {
                  click: () => {
                    this.flowComment = params.row.flowComment
                    this.flowCommentModel = true
                  },
                },
              })
            } else {
              return h("span", {}, "")
            }
          }
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('CREATE_DATE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "created",
          width: 180,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('PERSON_ACCOUNT');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "personEmail",
          width: 200,
          align: "center",
        },
      ],
      quotationList: [],
      deltaPn: "",
      vendorCode: "",
      fromRfqDate: "",
      toRfqDate: "",
      quotation: {},
      infoRecordMaintainModal: false,
      materialTypeList: []
    }
  },
  methods: {
    ...mapGetters(["getUserProfile", "getLanguage", "getSupervisorGroupUuid", "getSupervisorGroupId", "getModalSize"]),
    ...mapActions(["toggleLoading"]),
    changeSelectAll() {
      this.metalAttributeModel = false
      this.plasticAttributeModel = false
      if (this.selectAllType === 'APPROVE') {
        this.quotationList.forEach(quotation => {
          this.$set(quotation, "selectApprove", true);
          this.$set(quotation, "selectReject", false);
          this.$set(quotation, "action", "APPROVE");
        });
      } else if (this.selectAllType === 'REJECT') {
        this.quotationList.forEach(quotation => {
          this.$set(quotation, "selectApprove", false);
          this.$set(quotation, "selectReject", true);
          this.$set(quotation, "action", "REJECT");
        });
      }
    },
    changeApprove(value, index) {
      if (value === true) {
        this.quotationList[index].selectApprove = true;
        this.quotationList[index].selectReject = false;
        this.quotationList[index].action = "APPROVE";
      }
    },
    changeReject(value, index) {
      if (value === true) {
        this.quotationList[index].selectApprove = false;
        this.quotationList[index].selectReject = true;
        this.quotationList[index].action = "REJECT";
      }
    },
    updateDate(value, index, attr) {
      this.quotationList[index][attr] = value;
    },
    cleanSelect() {
      this.metalAttributeModel = false;
      this.plasticAttributeModel = false;
      this.selectAllType = "";
      this.quotationList.forEach(quotation => {
        this.$set(quotation, "selectApprove", false);
        this.$set(quotation, "selectReject", false);
        this.$set(quotation, "action", "");
      });
    },
    Submit() {
      const vueInstance = this;
      if (vueInstance.quotationList.some(quotation => quotation.action === undefined || quotation.action === "") === true) {
        AlertInfo(vueInstance, vueInstance.$t('CHOOSE'))
          .then(() => {});
        return;
      }
      vueInstance.toggleLoading(true);
      let quotationRequestListForApprove = [];
      let quotationRequestListForReject = [];
      vueInstance.quotationList.forEach(quotation => {
        switch (quotation.action) {
          case "APPROVE":
            quotationRequestListForApprove.push({
              uuid: quotation.uuid,
              comments: quotation.comments,
              validFrom: quotation.validFrom,
              validTo: quotation.validTo
            });
            break;
          case "REJECT":
            quotationRequestListForReject.push({
              uuid: quotation.uuid,
              comments: quotation.comments
            });
            break;
        }
      });
      vueInstance.$quotationHttp
        .post("/supervisor/group/submit", {
          groupId: vueInstance.quotationList[0].groupId,
          quotationRequestListForApprove: quotationRequestListForApprove,
          quotationRequestListForReject: quotationRequestListForReject
        })
        .then(response => {
          if (!response.data) {
            return;
          }
          vueInstance.$Swal.fire({
            icon: "info",
            title: vueInstance.$t("COMMON_MESSAGE_INFO"),
            html: "Group: " + response.data.content[0].id + ", " + response.data.content[0].status,
          })
          vueInstance.$router.push({
            name: "SUPERVISOR_MAINTAIN"
          });
        })
        .catch(error => {
          if (error.response.status == 444) {
            vueInstance.$Swal.fire({
              icon: "error",
              title: Translate("COMMON_MESSAGE_ERROR"),
              text: vueInstance.$buildErrorMessage(error)
            });
          }
        })
        .finally(() => {
          vueInstance.toggleLoading(false);
        });
    },
    confirmComments() {
      const vueInstance = this
      vueInstance.quotationList.forEach(temp => {
        if (temp.uuid == vueInstance.quotationUuid) {
          temp.comments = vueInstance.comments
        } else if (vueInstance.actionType && vueInstance.actionType == temp.action) {
          temp.comments = vueInstance.comments
        }
      })
    },
    confirmRemark() {
      this.quotationList.forEach(temp => {
        if (temp.id == this.quotationUuid) {
          temp.remark = this.remark
        }
      })
    },
    Back() {
      this.$router.push({
        name: "SUPERVISOR_MAINTAIN"
      });
    },
    clearAction(index) {
      this.quotationList[index].selectApprove = false;
      this.quotationList[index].selectReject = false;
      this.quotationList[index].action = "";
    },
    showCommentsModal(index, uuid) {
      this.comments = this.quotationList[index].comments ?? "";
      this.actionType = this.quotationList[index].action ?? "";
      this.quotationUuid = uuid;
      this.commentsModel = true;
    },
    showHistoryCommentsModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryQuotationLog(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.logComment = "";
          data.forEach(log => {
            if (log.quotationRole) {
              this.logComment = this.logComment + this.$t("ROLE") + ": " + log.quotationRole + ", ";
            }
            if (log.quotationRole) {
              this.logComment = this.logComment + this.$t("STATUS") + ": " + log.flowStatus + ", ";
            }
            if (log.groupAccount) {
              this.logComment = this.logComment + this.$t("GROUP_ACCOUNT") + ": " + log.groupAccount.code + "/" + log.groupAccount.companyName + ", ";
            }
            if (log.created) {
              this.logComment = this.logComment + this.$t("CREATE_DATE") + ": " + log.created + ": <br>";
            }
            if (log.flowComment) {
              this.logComment = this.logComment + log.flowComment;
            }
            this.logComment = this.logComment + "<hr style=\"border:1px dashed #000; height:1px\">";
          });
          this.logCommentModel = true;
        })
        .catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showInfoRecordMaintainModal(index, uuid) {
      this.quotation = this.quotationList[index].quotationDetailInfoRecordDto;
      this.infoRecordMaintainModal = true;
    },
    showPlmFilesModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryPlmDocuments(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.quotationDocuments = data;
          this.plmFileModel = true;
        }).catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showQuotationFilesModal(index) {
      this.supplierQuotationFiles = this.quotationList[index].supplierQuotationFiles.filter(attachment => !attachment.deleted);
      this.supplierQuotationFilesModel = true;
    },
    showReferenceFileModal(index) {
      this.cpcToSupervisorAttachments = this.quotationList[index].cpcToSupervisorAttachments.filter(attachment => !attachment.deleted);
      this.cpcToSupervisorAttachmentsModel = true;
    },
    showScalePriceModal(index) {
      this.scalePrices = this.quotationList[index].scalePrices;
      this.scalePricesModel = true;
    },
    showReferencePriceDataModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryReferencePrice(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.referencePrices = data;
          this.referencePriceModel = true;
        }).catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showRemarkModal(index, uuid) {
      this.remark = this.quotationList[index].remark;
      this.quotationUuid = uuid;
      this.remarkModel = true;
    },
    showLogModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryQuotationLog(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.quotationLogList = data;
          this.logModel = true;
        }).catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showCategoryAttributesModal(index, category, formNo) {
      switch (category) {
        case "METAL":
          this.$showMetalCategoryAttribute(this, formNo);
          break;
        case "PLASTIC":
          this.$showPlasticCategoryAttribute(this, formNo);
          break;
        case "PWB":
          this.$showPwbCategoryAttribute(this, formNo);
          break;
      }
    }
  },
  computed: {
    quotationView() {
      return this.quotationList && this.quotationList.length > 0
    },
    modalSize() {
      return this.getModalSize()
    },
    quotationColumnListForSimple() {
      return [{
        title: "#",
        align: "center",
        width: 40,
        tree: true,
      }, {
        title: this.$t("CLEAR"),
        align: "center",
        width: 80,
        slot: "clear"
      }, {
        title: this.$t("APPROVE"),
        align: "center",
        width: 80,
        slot: "approve"
      }, {
        title: this.$t("REJECT"),
        align: "center",
        width: 80,
        slot: "reject"
      }, {
        title: this.$t("COMMENTS"),
        align: "center",
        width: 90,
        slot: "comments"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("HISTORY"), 
            h("br"), 
            this.$t("COMMENTS")
          ]);
        },
        align: "center",
        width: 70,
        slot: "historyComments"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("QUOTATION"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "quotationFiles"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE"), 
            h("br"), 
            this.$t("FILE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referenceFile"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PLM"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "plmFiles"
      }, {
        title: this.$t("VALID_FROM"),
        align: "center",
        width: 120,
        slot: "validFrom"
      }, {
        title: this.$t("VALID_TO"),
        align: "center",
        width: 120,
        slot: "validTo"
      }, {
        title: this.$t("HIGH_PRICE"),
        align: "center",
        width: 60,
        slot: "highPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("TARGET"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 80,
        key: "targetPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("UNIT_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "unitPriceAndCurrency"
      }, {
        title: this.$t("DELTA_PN"),
        align: "center",
        width: 100,
        key: "deltaPart"
      }, {
        title: this.$t("OA_VENDOR_NAME"),
        align: "center",
        width: 90,
        key: "vendorName"
      }, {
        title: this.$t("VENDOR_PN"),
        align: "center",
        width: 80,
        key: "vendorPartNo"
      }, {
        title: this.$t("PLANT_CODE"),
        align: "center",
        width: 50,
        key: "plant"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE_PRICE"), 
            h("br"), 
            this.$t("DATA")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referencePriceData"
      }, {
        title: this.$t("ITEM_CATEGORY"),
        align: "center",
        width: 100,
        slot: "itemCategory"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PROCESS_DAYS"), 
            h("br"), 
            this.$t("CPC")
          ]);
        },
        align: "center",
        width: 80,
        key: "processDays"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("AVERAGE_PURCHASE_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "averagePurchasePriceAndCurrency"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("SCALE"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "scalePrice"
      }, {
        title: this.$t("RAW_MATERIAL_TYPE"),
        align: "center",
        width: 80,
        key: "rawMaterialType"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("RAW_MATERIAL"), 
            h("br"), 
            this.$t("PRICE_UNIT")
          ]);
        },
        align: "center",
        width: 80,
        slot: "rawMaterialPriceAndUnit"
      }, {
        title: this.$t("RM_REMARK"),
        align: "center",
        width: 60,
        slot: "remark"
      }, {
        title: this.$t("YMGR_REMARK"),
        align: "center",
        width: 60,
        slot: "ymgrRemark"
      }, {
        title: this.$t("LOG"),
        align: "center",
        width: 60,
        slot: "log"
      }, {
        title: this.$t("CATEGORY_ATTRIBUTES"),
        align: "center",
        width: 50,
        slot: "categoryAttributes"
      }, {
        title: this.$t("INFO_RECORD"),
        align: "center",
        width: 60,
        slot: "infoRecord"
      }, {
        title: this.$t("RFQ_TYPE"),
        align: "center",
        width: 85,
        slot: "rfqType"
      }, {
        title: this.$t("CATEGORY"),
        align: "center",
        width: 60,
        key: "category"
      }, {
        title: this.$t("FORM_NO"),
        align: "center",
        width: 90,
        key: "formNo"
      }];
    },
    quotationColumnList() {
      return [{
        title: "#",
        align: "center",
        width: 40,
        tree: true,
      }, {
        title: this.$t("ACTION"),
        align: "center",
        width: 80,
        slot: "action"
      }, {
        title: this.$t("COMMENTS"),
        align: "center",
        width: 90,
        slot: "comments"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("HISTORY"), 
            h("br"), 
            this.$t("COMMENTS")
          ]);
        },
        align: "center",
        width: 70,
        slot: "historyComments"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PROCESS_DAYS"), 
            h("br"), 
            this.$t("CPC")
          ]);
        },
        align: "center",
        width: 80,
        key: "processDays"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("VALID_FROM"), 
            h("br"), 
            this.$t("VALID_TO")
          ]);
        },
        align: "center",
        width: 140,
        slot: "validFromAndValidTo"
      }, {
        title: this.$t("HIGH_PRICE"),
        align: "center",
        width: 60,
        slot: "highPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("TARGET"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 80,
        key: "targetPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("UNIT_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "unitPriceAndCurrency"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE_PRICE"), 
            h("br"), 
            this.$t("DATA")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referencePriceData"
      }, {
        title: this.$t("GROUP_COMPARE") + "(%)",
        align: "center",
        width: 60,
        key: "comparePrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("QUOTATION"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "quotationFiles"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PLM"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "plmFiles"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("DELTA_PN"), 
            h("br"), 
            this.$t("PN_DESCRIPTION")
          ]);
        },
        align: "center",
        width: 100,
        slot: "deltaPartAndDeltaPartDescription"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("VENDOR_CODE"), 
            h("br"), 
            this.$t("OA_VENDOR_NAME")
          ]);
        },
        align: "center",
        width: 90,
        slot: "vendorCodeAndVendorName"
      }, {
        title: this.$t("VENDOR_PN"),
        align: "center",
        width: 80,
        key: "vendorPartNo"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("AVERAGE_PURCHASE_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "averagePurchasePriceAndCurrency"
      }, {
        title: this.$t("PLANT_CODE"),
        align: "center",
        width: 50,
        key: "plant"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE"), 
            h("br"), 
            this.$t("FILE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referenceFile"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("SCALE"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "scalePrice"
      }, {
        title: this.$t("RAW_MATERIAL_TYPE"),
        align: "center",
        width: 80,
        key: "rawMaterialType"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("RAW_MATERIAL"), 
            h("br"), 
            this.$t("PRICE_UNIT")
          ]);
        },
        align: "center",
        width: 80,
        slot: "rawMaterialPriceAndUnit"
      }, {
        title: this.$t("RM_REMARK"),
        align: "center",
        width: 60,
        slot: "remark"
      }, {
        title: this.$t("YMGR_REMARK"),
        align: "center",
        width: 60,
        slot: "ymgrRemark"
      }, {
        title: this.$t("LOG"),
        align: "center",
        width: 60,
        slot: "log"
      }, {
        title: this.$t("CATEGORY_ATTRIBUTES"),
        align: "center",
        width: 50,
        slot: "categoryAttributes"
      }, {
        title: this.$t("ITEM_CATEGORY"),
        align: "center",
        width: 100,
        slot: "itemCategory"
      }, {
        title: this.$t("INFO_RECORD"),
        align: "center",
        width: 60,
        slot: "infoRecord"
      }, {
        title: this.$t("RFQ_TYPE"),
        align: "center",
        width: 85,
        slot: "rfqType"
      }, {
        title: this.$t("CATEGORY"),
        align: "center",
        width: 60,
        key: "category"
      }, {
        title: this.$t("FORM_NO"),
        align: "center",
        width: 90,
        key: "formNo"
      }];
    }
  }
}

function getDefaultValidTo() {
  let today = new Date();
  if ((today.getMonth() + 1) > 9) {
    return moment([today.getFullYear() + 1, 11, 31]).format("YYYY-MM-DD");
  } else {
    return moment([today.getFullYear(), 11, 31]).format("YYYY-MM-DD");
  }
}
</script>

<style scoped>
.overflow-visible{
  overflow: visible;
}
</style>
